/* eslint-disable tailwindcss/no-arbitrary-value */
'use client';

import { Tooltip } from '@material-tailwind/react';
import {
  LeadCapturePage_attributeList,
  LeadCapturePage_gift,
  LeadCapturePage_offer,
} from '__generated__/graphql';
import { JSX, useState } from 'react';
import { t } from 'utils/locale';
import { Translated } from 'utils/staticText';
import { getVerticalNameFromProductUid } from '../../lib/product';
import {
  ITrackingProductPageBaseParameters,
  trackProductCardSeeAllEligibleRewards,
} from '../../lib/tracking';
import { ProductDialog } from '../ProductDialog';
import { ProductDialogStaticText } from '../ProductDialog/i18n';
import { CatalogProductFragment } from '../types';
import { OfferText } from './i18n';
import {
  CatalogueProductOffers,
  getAllActiveOffers,
  getHighestGiftValue,
} from '$/(application)/components/rewardOffersUtils';
import Diamond from '$/components/icons/Diamond';

export function RewardOffers(props: {
  product: CatalogProductFragment;
  attributes: LeadCapturePage_attributeList['attributeList'];
  offer: LeadCapturePage_offer;
  gifts: LeadCapturePage_gift[];
  staticText: Translated<typeof OfferText>;
  productDialogStaticText: Translated<typeof ProductDialogStaticText>;
  tracking: ITrackingProductPageBaseParameters;
  currencyDisplay: string;
}): JSX.Element {
  const [showProductDetail, setShowProductDetail] = useState<boolean>(false);
  const [scrollToSection, setScrollToSection] = useState<string | null>(null);
  const verticalCode = getVerticalNameFromProductUid(props.product?.productUid);

  /*offers rewards*/
  const allActiveOffers = getAllActiveOffers(
    props.product as CatalogueProductOffers,
  );

  const highestGiftValue = getHighestGiftValue(allActiveOffers);

  const {
    dialogSeeMore,
    dialogSeeLess,
    dialogRewardsTitle,
    dialogRewardsDescription,
    dialogRewardsOption,
    dialogRewardsTC,
  } = props.productDialogStaticText;

  return (
    <section className="mhg-alpha-rewards-lead-capture-section">
      <div className="mhg-alpha-rewards-lead-capture-heading-container">
        <div className="mhg-alpha-display-1 not-italic text-[--text-primary]">
          {highestGiftValue?.price
            ? t(props.staticText.offerHeadingDynamic, {
                giftValue:
                  props.currencyDisplay +
                  Number(highestGiftValue.price).toLocaleString(),
              })
            : props.staticText.offerHeading}
        </div>
        <div className="mhg-alpha-rewards-lead-capture-subheading">
          {t(props.staticText.offerSubHeading, {
            verticalName:
              props.staticText[`offerVertialName${verticalCode}`] ??
              props.staticText.offerVerticalNameDefault,
          })}
        </div>
      </div>
      <div className="mhg-alpha-rewards-lead-capture-card bg-[--fill-base]">
        <div className="mhg-alpha-rewards-lead-capture-offer-body md:px-[--spacing-padding-x] md:py-[--spacing-padding-y]">
          {props.gifts.length > 0 && (
            <div className="mhg-alpha-rewards-lead-capture-offer-content mhg-alpha-rewards-lead-capture-offer-content-no-scrollbar md:mhg-alpha-rewards-lead-capture-offer-content-md max-w-[calc(100vw-35px)] md:max-w-full">
              <div className="mhg-alpha-rewards-lead-capture-offer-content-no-scrollbar flex content-center items-center gap-[--spacing-gap-x-sm] self-stretch overflow-x-scroll whitespace-nowrap md:grid md:grid-cols-4 md:gap-x-[--spacing-gap-x-sm] md:gap-y-[--spacing-gap-y-sm] lg:grid-cols-5">
                {props.gifts.map((gift) => (
                  <div
                    key={gift.giftUid}
                    className="flex h-24 w-30 flex-1 flex-col items-center justify-end rounded-[--radius-button] border border-solid border-[--borders-default] bg-[--fill-card] first:ml-spacing/margin/x last:mr-spacing/margin/x md:h-34 md:w-auto md:first:ml-0 md:last:mr-0"
                  >
                    <div className="flex h-19 items-center justify-center gap-[10px] self-stretch p-[--button-padding-regular-x-icon] md:h-27">
                      <Tooltip
                        className="flex max-w-[--general-4-col-width] items-end justify-center rounded-[--radius-tooltip] px-[--spacing-padding-x-sm] py-[--spacing-padding-y-sm] shadow-md bg-[--fill-card]"
                        placement="top-start"
                        content={
                          <div>
                            <span className="text-text/secondary-lighter">
                              {props.offer.offerTitle}
                            </span>
                            <br></br>
                            <span className="font-semibold text-text/primary">
                              {gift.giftTitle}
                            </span>
                          </div>
                        }
                      >
                        <img
                          className="self-stretch object-contain"
                          src={gift.mobileImage}
                          alt={gift.giftTitle}
                        />
                      </Tooltip>
                    </div>
                    <div className="flex items-center justify-center self-stretch overflow-hidden rounded-b-[inherit] px-[--spacing-padding-x-xs] py-[--button-padding-toggle] text-center bg-[--fill-alternative-1] sm:min-w-30">
                      <span className="mhg-alpha-rewards-lead-capture-offer-image-title-text w-full truncate text-[--text-secondary]">
                        {gift.giftValue?.price
                          ? `Worth ${gift.giftValue.prefix}${Number(gift.giftValue.price).toLocaleString()}`
                          : gift.giftTitle}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="mhg-alpha-rewards-lead-capture-card-footer">
          <button
            type="button"
            className="mhg-alpha-rewards-lead-capture-card-footer-content bg-colors/neutral-white"
            onClick={() => {
              setScrollToSection('rewards');
              setShowProductDetail(true);
              trackProductCardSeeAllEligibleRewards(props.tracking);
            }}
          >
            <Diamond className="mhg-alpha-rewards-lead-capture-card-footer-icon" />
            <span className="mhg-alpha-rewards-lead-capture-card-footer-content-label">
              {props.staticText.offerSeeAll} ({allActiveOffers.length})
            </span>
          </button>
        </div>
      </div>
      <ProductDialog
        setOpen={setShowProductDetail}
        scrollToSection={scrollToSection}
        isOpen={showProductDetail}
        product={props.product}
        attributes={props.attributes}
        staticText={{
          dialogSeeMore,
          dialogSeeLess,
          dialogRewardsTitle,
          dialogRewardsDescription,
          dialogRewardsOption,
          dialogRewardsTC,
        }}
        tracking={props.tracking}
      />
    </section>
  );
}
